'use client';

import { type FC, useMemo } from 'react';
import { SkinImage } from '../../ui/SkinImage';
import { Button } from '../../ui/Button';
import { Credits } from '../../Credits';
import { FaClock, FaSteam, FaUsers, IoPricetag } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { useCurrentTime } from '../../../hooks/useCurrentTime';
import { parseItemDetails, abbreviateWearCondition, ruPlural } from '../../../utils/formatters';
import { rarityGradient } from '../../../utils/rarityGradient';
import { countdown } from './raffleVisuals';
import translations from './raffle.translations';
import type { RushRaffleRoundListItem } from '../../../services/generated/Api';

interface RaffleCardProps {
  round: RushRaffleRoundListItem;
  onOpen: (roundId: string) => void;
  onPlay: (ticket: number) => void;
}

/** Vertical raffle card — DailyGiveawayCard's visual language (rarity gradient, big skin
 *  image, weapon/condition + name + price row) with raffle chrome: entry badge, countdown,
 *  tickets/players stats, play CTA. */
export const RaffleCard: FC<RaffleCardProps> = ({ round, onOpen, onPlay }) => {
  const { t } = useLocalTranslation(translations);
  const isDesktop = useIsDesktop();
  const now = useCurrentTime();
  const { prize } = round;

  const parsed = useMemo(() => parseItemDetails(prize.hashName), [prize.hashName]);
  const conditionLabel = parsed.condition ? abbreviateWearCondition(parsed.condition) : '';
  const playersWord = [t('playersOne'), t('playersFew'), t('playersMany')][ruPlural(round.participantsCount)];

  if (isDesktop) {
    return (
      <div
        className="group relative flex w-full flex-row overflow-hidden rounded-[21px] text-white"
        style={{ background: rarityGradient(prize.rarityColor) }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
        />
        <div className="relative z-[1] flex min-w-0 flex-1 flex-col gap-4 p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-xs font-bold text-white backdrop-blur">
              {t('ticketBadgeLabel')} <Credits amount={round.ticket} iconSize={13} className="font-extrabold" />
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-xs font-bold tabular-nums text-white backdrop-blur">
              <FaClock className="text-[11px] text-white/70" />
              {countdown(round.endsAt, now)}
            </span>
          </div>

          <div>
            {(parsed.weaponName || conditionLabel) && (
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase leading-4 tracking-[1px] text-white/80">
                {parsed.weaponName && <span>{parsed.weaponName}</span>}
                {conditionLabel && <span className="text-white">{conditionLabel}</span>}
              </div>
            )}
            <h3 className="m-0 text-xl font-bold leading-7 tracking-[-0.4px] text-white">
              {parsed.skinName || parsed.weaponName || prize.name}
            </h3>
            <div className="mt-1.5 flex items-center gap-1.5 whitespace-nowrap">
              <Credits amount={prize.priceCredits} iconSize={14} className="text-base font-semibold" />
              {prize.steamPriceUsd != null && (
                <span className="flex items-center text-sm text-white/80">
                  (<FaSteam size={12} className="mx-1" />${prize.steamPriceUsd.toFixed(2)})
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => onOpen(round.roundId)} className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-left transition active:bg-white/25">
              <IoPricetag className="shrink-0 text-lg text-white/80" />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-xs text-white/70">{t('yourTicketsLabel')}</div>
                <div className="text-lg font-bold text-white">{round.myTickets}</div>
              </div>
            </button>
            <button type="button" onClick={() => onOpen(round.roundId)} className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-left transition active:bg-white/25">
              <FaUsers className="shrink-0 text-lg text-white/80" />
              <div className="min-w-0 leading-tight">
                <div className="truncate text-xs text-white/70">{t('participantsLabelTop')}</div>
                <div className="truncate text-lg font-bold text-white">{round.participantsCount} {playersWord}</div>
              </div>
            </button>
          </div>

          <div className="mt-auto">
            <Button
              type="button"
              size="m"
              stretched
              onClick={(e) => { e.stopPropagation(); onPlay(round.ticket); }}
              className="!bg-white font-semibold !text-black"
            >
              <span className="inline-flex items-center gap-1.5">
                {t('playCta')} <Credits amount={round.ticket} iconSize={15} />
              </span>
            </Button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onOpen(round.roundId)}
          className="relative flex w-[440px] shrink-0 items-center justify-center p-2"
        >
          <div
            className="relative flex h-full w-full items-center justify-center transition-transform duration-300 group-hover:scale-105"
            style={{ filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.55))' }}
          >
            <SkinImage name={prize.name} src={prize.imageUrl} imgClassName="w-full h-full object-contain" />
          </div>
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative flex w-full flex-col overflow-hidden rounded-[21px] p-0 text-white"
      style={{ background: rarityGradient(prize.rarityColor) }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
      />

      <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-xs font-bold text-white backdrop-blur">
        {t('ticketBadgeLabel')} <Credits amount={round.ticket} iconSize={13} className="font-extrabold" />
      </span>
      <span className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/35 px-2.5 py-1 text-xs font-bold tabular-nums text-white backdrop-blur">
        <FaClock className="text-[11px] text-white/70" />
        {countdown(round.endsAt, now)}
      </span>

      <div className="relative flex w-full flex-col">
        <button
          type="button"
          onClick={() => onOpen(round.roundId)}
          className="flex h-[220px] items-center justify-center px-4 pt-4 transition active:scale-[0.98]"
          style={{ filter: 'drop-shadow(0 12px 20px rgba(0,0,0,0.55))' }}
        >
          <SkinImage name={prize.name} src={prize.imageUrl} imgClassName="w-full h-full object-contain" />
        </button>

        <div className="mt-1 flex items-end justify-between gap-3 px-4">
          <div className="flex min-w-0 flex-col">
            {(parsed.weaponName || conditionLabel) && (
              <div className="flex items-center gap-1.5 truncate text-[11px] font-semibold uppercase leading-4 tracking-[1px] text-white/80">
                {parsed.weaponName && <span>{parsed.weaponName}</span>}
                {conditionLabel && <span className="text-white">{conditionLabel}</span>}
              </div>
            )}
            <h3 className="m-0 truncate text-2xl font-bold leading-7 tracking-[-0.4px] text-white">
              {parsed.skinName || parsed.weaponName || prize.name}
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-1 whitespace-nowrap text-lg font-bold leading-7 text-white">
            <Credits amount={prize.priceCredits} iconSize={16} />
            {prize.steamPriceUsd != null && (
              <span className="flex items-center text-sm font-semibold text-white/80">
                (<FaSteam size={12} className="mx-1" />${prize.steamPriceUsd.toFixed(2)})
              </span>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2 px-4">
          <button type="button" onClick={() => onOpen(round.roundId)} className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-left transition active:bg-white/25">
            <IoPricetag className="shrink-0 text-lg text-white/80" />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-xs text-white/70">{t('yourTicketsLabel')}</div>
              <div className="text-lg font-bold text-white">{round.myTickets}</div>
            </div>
          </button>
          <button type="button" onClick={() => onOpen(round.roundId)} className="flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-left transition active:bg-white/25">
            <FaUsers className="shrink-0 text-lg text-white/80" />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-xs text-white/70">{t('participantsLabelTop')}</div>
              <div className="truncate text-lg font-bold text-white">{round.participantsCount} {playersWord}</div>
            </div>
          </button>
        </div>

        <div className="px-4 pb-4 pt-3">
          <Button
            type="button"
            size="l"
            stretched
            onClick={(e) => { e.stopPropagation(); onPlay(round.ticket); }}
            className="h-[45px] !bg-white font-semibold !text-black"
          >
            <span className="inline-flex items-center gap-1.5">
              {t('playCta')} <Credits amount={round.ticket} iconSize={15} />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RaffleCard;
